import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, Coins, Menu, Globe, Settings, History, Key, LogOut, Target } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

export default function Navigation() {
  const { user, logout } = useAuth();
  const { language, changeLanguage, LANGUAGES, t } = useLanguage();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const avatarRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (avatarRef.current && !avatarRef.current.contains(e.target)) {
        setAvatarOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const avatarLetter = user?.name ? user.name[0].toUpperCase() : '?';

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 group">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-white" />
          </div>
          <span className="text-2xl font-bold font-heading gradient-text">Alyse</span>
        </button>

        {/* Center nav links (absolute center) */}
        <div className="hidden md:flex items-center gap-6 absolute left-1/2 -translate-x-1/2">
          <button onClick={() => navigate('/mission')} className="text-slate-300 hover:text-white transition-colors">
            {t('nav_mission')}
          </button>
          <button onClick={() => navigate('/pricing')} className="text-slate-300 hover:text-white transition-colors">
            {t('nav_pricing')}
          </button>
          {user && (
            <button onClick={() => navigate('/credits')} className="text-slate-300 hover:text-white transition-colors flex items-center gap-2">
              <Coins className="w-4 h-4" /> {t('nav_credits')}
            </button>
          )}
        </div>

        <div className="flex items-center gap-4">
          {/* Language selector */}
          <div className="relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full hover:bg-white/5 transition-colors text-slate-300 hover:text-white"
            >
              <Globe className="w-4 h-4" />
              <span className="text-xs font-medium">{LANGUAGES[language]?.flag}</span>
            </button>
            {langOpen && (
              <div className="absolute right-0 top-full mt-2 py-1 rounded-xl bg-slate-800 border border-white/10 shadow-xl z-50 min-w-[140px]">
                {Object.entries(LANGUAGES).map(([code, info]) => (
                  <button
                    key={code}
                    onClick={() => { changeLanguage(code); setLangOpen(false); }}
                    className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-white/5 transition-colors ${
                      language === code ? 'text-emerald-400' : 'text-slate-300'
                    }`}
                  >
                    <span>{info.flag}</span>
                    <span>{info.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Credits badge (logged in, desktop) */}
          {user && (
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
              <Coins className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 text-sm font-medium">{user.credits}</span>
            </div>
          )}

          {/* Avatar menu or Login button */}
          {user ? (
            <div className="relative" ref={avatarRef}>
              <button
                onClick={() => setAvatarOpen(!avatarOpen)}
                className="w-9 h-9 rounded-full overflow-hidden flex items-center justify-center ring-2 ring-white/10 hover:ring-emerald-500/50 transition-all"
              >
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center text-white font-bold text-sm">
                    {avatarLetter}
                  </div>
                )}
              </button>

              <AnimatePresence>
                {avatarOpen && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -4 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -4 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 top-full mt-2 w-64 rounded-xl bg-slate-800 border border-white/10 shadow-2xl z-50 overflow-hidden"
                  >
                    {/* Header */}
                    <div className="px-4 py-3 border-b border-white/5">
                      <p className="text-white font-medium text-sm truncate">{user.name}</p>
                      <p className="text-slate-400 text-xs truncate">{user.email}</p>
                      <div className="mt-1.5 inline-block px-2 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/20">
                        <span className="text-blue-400 text-xs font-medium capitalize">{user.plan}</span>
                      </div>
                    </div>

                    {/* Menu items */}
                    <div className="py-1">
                      <button
                        onClick={() => { navigate('/settings'); setAvatarOpen(false); }}
                        className="w-full px-4 py-2.5 text-left text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors flex items-center gap-3"
                      >
                        <Settings className="w-4 h-4" /> {t('menu_settings')}
                      </button>
                      <button
                        onClick={() => { navigate('/history'); setAvatarOpen(false); }}
                        className="w-full px-4 py-2.5 text-left text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors flex items-center gap-3"
                      >
                        <History className="w-4 h-4" /> {t('menu_history')}
                      </button>
                      <button
                        onClick={() => { navigate('/settings?tab=api'); setAvatarOpen(false); }}
                        className="w-full px-4 py-2.5 text-left text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors flex items-center gap-3"
                      >
                        <Key className="w-4 h-4" /> {t('menu_api_key')}
                      </button>
                    </div>

                    {/* Separator + Logout */}
                    <div className="border-t border-white/5 py-1">
                      <button
                        onClick={() => { logout(); setAvatarOpen(false); navigate('/'); }}
                        className="w-full px-4 py-2.5 text-left text-sm text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-3"
                      >
                        <LogOut className="w-4 h-4" /> {t('menu_logout')}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-2 rounded-full bg-gradient-to-r from-emerald-500 to-blue-600 text-white font-medium hover:opacity-90 transition-opacity"
            >
              {t('nav_login')}
            </button>
          )}

          {/* Mobile hamburger */}
          <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden p-2 rounded-lg hover:bg-white/5">
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t border-white/5"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              <button onClick={() => { navigate('/mission'); setMenuOpen(false); }} className="text-left text-slate-300 flex items-center gap-2">
                <Target className="w-4 h-4" /> {t('nav_mission')}
              </button>
              <button onClick={() => { navigate('/pricing'); setMenuOpen(false); }} className="text-left text-slate-300">{t('nav_pricing')}</button>
              {user && (
                <>
                  <button onClick={() => { navigate('/credits'); setMenuOpen(false); }} className="text-left text-slate-300">{t('nav_credits')} ({user.credits})</button>
                  <button onClick={() => { navigate('/settings'); setMenuOpen(false); }} className="text-left text-slate-300 flex items-center gap-2">
                    <Settings className="w-4 h-4" /> {t('menu_settings')}
                  </button>
                  <button onClick={() => { navigate('/history'); setMenuOpen(false); }} className="text-left text-slate-300 flex items-center gap-2">
                    <History className="w-4 h-4" /> {t('menu_history')}
                  </button>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
